import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { TablePagination } from '../components/ui/table-pagination';
import { toast } from 'sonner';
import NotificationBell from '../components/common/NotificationBell';
import {
  ArrowLeft, AlertTriangle, Clock, CheckCircle, XCircle, Plus, Search,
  Home as HomeIcon, Calendar, User, Wallet, Briefcase, MessageCircle, FileText,
  CreditCard, UserCog, HelpCircle, Bug
} from 'lucide-react';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const statusColors = {
  open: 'bg-yellow-100 text-yellow-800',
  pending: 'bg-yellow-100 text-yellow-800',
  investigating: 'bg-blue-100 text-blue-800',
  resolved: 'bg-green-100 text-green-800',
  closed: 'bg-stone-100 text-stone-800',
  rejected: 'bg-red-100 text-red-800',
};

const statusIcons = {
  open: Clock,
  pending: Clock,
  investigating: MessageCircle,
  resolved: CheckCircle,
  closed: XCircle,
  rejected: XCircle,
};

const categoryConfig = {
  booking: { label: 'Booking Issue', icon: Calendar, color: 'bg-blue-100 text-blue-700' },
  withdrawal: { label: 'Withdrawal Problem', icon: CreditCard, color: 'bg-purple-100 text-purple-700' },
  account: { label: 'Account Issue', icon: UserCog, color: 'bg-amber-100 text-amber-700' },
  app_issue: { label: 'App/UX Problem', icon: Bug, color: 'bg-red-100 text-red-700' },
  other: { label: 'Other', icon: HelpCircle, color: 'bg-stone-100 text-stone-700' },
};

const issueTypesByCategory = {
  booking: [
    'Service not completed',
    'Poor quality work',
    'Overcharged',
    'Provider no-show',
    'Customer no-show',
    'Damage to property',
    'Unprofessional behavior',
    'Payment issue',
    'Other booking issue'
  ],
  withdrawal: [
    'Withdrawal not received',
    'Wrong amount received',
    'Withdrawal stuck/pending',
    'Bank account issue',
    'Other withdrawal issue'
  ],
  account: [
    'Cannot login',
    'Profile update issue',
    'Verification problem',
    'Password reset issue',
    'Account suspended',
    'Privacy concern',
    'Other account issue'
  ],
  app_issue: [
    'App crashing',
    'Feature not working',
    'Slow performance',
    'Display/UI issue',
    'Navigation problem',
    'Notification issue',
    'Other app issue'
  ],
  other: [
    'General inquiry',
    'Suggestion/Feedback',
    'Partnership inquiry',
    'Other'
  ]
};

export default function Disputes() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [disputes, setDisputes] = useState([]);
  const [allDisputes, setAllDisputes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [form, setForm] = useState({ 
    booking_id: '', 
    reason: '', 
    description: '',
    category: 'booking'
  });
  const [submitting, setSubmitting] = useState(false);
  
  // Filters & Pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 10;

  // Warranty Claims
  const [activeTab, setActiveTab] = useState('tickets');
  const [warrantyClaims, setWarrantyClaims] = useState([]);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [providerResponse, setProviderResponse] = useState('');
  const [isRespondingToClaim, setIsRespondingToClaim] = useState(false);

  const isProvider = user?.role === 'provider';

  const fetchDisputes = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/disputes`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      setAllDisputes(res.data);
    } catch (e) {
      console.error('Failed to fetch disputes:', e);
      if (e.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        navigate('/login');
      }
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  const fetchWarrantyClaims = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/warranty-claims`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      setWarrantyClaims(res.data || []);
    } catch (e) {
      console.error('Failed to fetch warranty claims:', e);
    }
  }, []);

  // Apply filters
  useEffect(() => {
    let filtered = allDisputes;
    
    // Apply search
    if (searchTerm) {
      filtered = filtered.filter(d => 
        d.reason.toLowerCase().includes(searchTerm.toLowerCase()) ||
        d.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (d.booking_id && d.booking_id.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(d => d.status === statusFilter);
    }
    
    // Apply category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(d => d.category === categoryFilter);
    }
    
    // Calculate pagination
    setTotalPages(Math.ceil(filtered.length / itemsPerPage));
    
    // Paginate
    const start = (page - 1) * itemsPerPage;
    setDisputes(filtered.slice(start, start + itemsPerPage));
  }, [allDisputes, searchTerm, statusFilter, categoryFilter, page]);

  const fetchBookings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/bookings`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      setBookings(res.data.filter(b => b.status === 'completed' || b.status === 'in_progress'));
    } catch (e) {
      console.error('Failed to fetch bookings:', e);
    }
  }, []);

  useEffect(() => {
    fetchDisputes();
    fetchWarrantyClaims();
  }, [fetchDisputes, fetchWarrantyClaims]);

  const handleCreate = async () => {
    if (!form.reason || !form.description) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    // Booking is only required for booking category
    if (form.category === 'booking' && !form.booking_id) {
      toast.error('Please select a booking for booking-related issues');
      return;
    }
    
    setSubmitting(true);
    try {
      const payload = {
        reason: form.reason,
        description: form.description,
        category: form.category
      };
      
      // Only include booking_id if it's a booking-related issue
      if (form.category === 'booking' && form.booking_id) {
        payload.booking_id = form.booking_id;
      }
      
      await axios.post(`${API}/disputes`, payload, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      toast.success('Support ticket submitted successfully');
      setShowCreateDialog(false);
      setForm({ booking_id: '', reason: '', description: '', category: 'booking' });
      fetchDisputes();
    } catch (e) {
      console.error('Failed to submit ticket:', e);
      toast.error(e.response?.data?.detail || 'Failed to submit ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRespondToClaim = async (claimId) => {
    if (!providerResponse.trim()) { toast.error('Please enter your response'); return; }
    setIsRespondingToClaim(true);
    try {
      await axios.post(`${API}/warranty-claims/${claimId}/respond`, { response: providerResponse }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      toast.success('Response submitted successfully');
      setSelectedClaim(null);
      setProviderResponse('');
      fetchWarrantyClaims();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit response');
    } finally {
      setIsRespondingToClaim(false);
    }
  };


  const openCreateDialog = () => {
    fetchBookings();
    setShowCreateDialog(true);
  };
  
  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-PH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const navItems = isProvider
    ? [
        { icon: HomeIcon, label: 'Home', path: '/provider' },
        { icon: Briefcase, label: 'Jobs', path: '/provider/jobs' },
        { icon: AlertTriangle, label: 'Support', path: '/disputes', active: true },
        { icon: User, label: 'Profile', path: '/provider/profile' }
      ]
    : [
        { icon: HomeIcon, label: 'Home', path: '/home' },
        { icon: Calendar, label: 'Bookings', path: '/bookings' },
        { icon: AlertTriangle, label: 'Support', path: '/disputes', active: true },
        { icon: User, label: 'Profile', path: '/profile' }
      ];

  return (
    <div className="min-h-screen bg-stone-50 pb-20" data-testid="disputes-page">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 px-4 py-3 sticky top-0 z-40">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(isProvider ? '/provider' : '/home')} className="w-10 h-10 rounded-full hover:bg-stone-100 flex items-center justify-center">
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </button>
            <div>
              <h1 className="font-bold text-slate-900 text-lg font-[Poppins]">Help & Support</h1>
              <p className="text-xs text-slate-500">Get help with bookings, account, or app issues</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell />
            <Button size="sm" onClick={openCreateDialog} data-testid="file-dispute-btn">
              <Plus className="w-4 h-4 mr-1" /> New Ticket
            </Button>
          </div>
        </div>
      </header>

      <main className="px-4 py-4 max-w-lg mx-auto space-y-4">
        {/* Tab Switcher */}
        <div className="flex gap-1 bg-white rounded-xl p-1 shadow-sm" data-testid="support-tabs">
          <button
            onClick={() => setActiveTab('tickets')}
            className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${activeTab === 'tickets' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-stone-100'}`}
            data-testid="tab-tickets"
          >
            <AlertTriangle className="w-3.5 h-3.5" />
            Tickets
          </button>
          <button
            onClick={() => setActiveTab('warranty')}
            className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${activeTab === 'warranty' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-stone-100'}`}
            data-testid="tab-warranty-claims"
          >
            <FileText className="w-3.5 h-3.5" />
            Warranty
            {warrantyClaims.filter(c => c.status === 'open' || (isProvider && c.status === 'open')).length > 0 && (
              <span className="px-1.5 py-0.5 bg-red-500 text-white text-xs rounded-full ml-1">
                {warrantyClaims.filter(c => c.status === 'open').length}
              </span>
            )}
          </button>
        </div>

        {activeTab === 'tickets' && (
        <>
        {/* Search & Filters */}
        <div className="space-y-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search tickets..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
              className="pl-10 h-10"
              data-testid="search-tickets"
            />
          </div>
          <div className="flex gap-2">
            <select
              value={categoryFilter}
              onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }}
              className="flex-1 h-10 px-3 border border-stone-200 rounded-lg text-sm"
              data-testid="category-filter"
            >
              <option value="all">All Categories</option>
              <option value="booking">Booking Issues</option>
              <option value="withdrawal">Withdrawal</option>
              <option value="account">Account</option>
              <option value="app_issue">App/UX</option>
              <option value="other">Other</option>
            </select>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              className="flex-1 h-10 px-3 border border-stone-200 rounded-lg text-sm"
              data-testid="status-filter"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="investigating">Investigating</option>
              <option value="resolved">Resolved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>
        
        {/* Tickets List */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
          </div>
        ) : disputes.length === 0 ? (
          <div className="text-center py-12">
            <AlertTriangle className="w-12 h-12 text-slate-200 mx-auto mb-3" />
            <p className="text-slate-500 text-sm">
              {searchTerm || statusFilter !== 'all' || categoryFilter !== 'all' 
                ? 'No tickets match your filters' 
                : 'No support tickets yet'}
            </p>
            <p className="text-slate-400 text-xs mt-1">Submit a ticket if you need any help</p>
          </div>
        ) : (
          <div className="space-y-3">
            {disputes.map(d => {
              const StatusIcon = statusIcons[d.status] || Clock;
              const catConfig = categoryConfig[d.category] || categoryConfig.other;
              const CatIcon = catConfig.icon;
              return (
                <div 
                  key={d.id} 
                  className="bg-white rounded-2xl p-4 shadow-sm cursor-pointer hover:shadow-md transition-shadow" 
                  data-testid={`dispute-${d.id}`}
                  onClick={() => setSelectedDispute(d)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${catConfig.color}`}>
                        <CatIcon className="w-3 h-3" />
                        {catConfig.label}
                      </span>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[d.status] || statusColors.pending}`}>
                        <StatusIcon className="w-3 h-3" />
                        {d.status.charAt(0).toUpperCase() + d.status.slice(1)}
                      </span>
                    </div>
                    <span className="text-xs text-slate-400">#{d.id?.slice(0, 8)}</span>
                  </div>
                  <p className="font-semibold text-slate-900 text-sm">{d.reason}</p>
                  <p className="text-sm text-slate-600 mt-1 line-clamp-2">{d.description}</p>
                  <div className="flex items-center gap-4 text-xs text-slate-400 mt-3">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5" />
                      {formatDate(d.created_at)}
                    </span>
                    {d.booking_id && (
                      <span className="flex items-center gap-1">
                        <FileText className="w-3.5 h-3.5" />
                        Booking #{d.booking_id?.slice(0, 8)}
                      </span>
                    )}
                  </div>
                  {d.resolution && (
                    <div className="mt-3 bg-green-50 rounded-lg p-3">
                      <p className="text-xs font-medium text-green-800">Resolution:</p>
                      <p className="text-sm text-green-700">{d.resolution}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center mt-4">
            <TablePagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          </div>
        )}
        </>
        )}

        {activeTab === 'warranty' && (
          <div className="space-y-3">
            {warrantyClaims.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                <p className="text-slate-500 text-sm">No warranty claims yet</p>
                <p className="text-slate-400 text-xs mt-1">Warranty claims from completed bookings will appear here</p>
              </div>
            ) : (
              warrantyClaims.map(claim => {
                const statusColor = {
                  open: 'bg-yellow-100 text-yellow-800',
                  provider_responded: 'bg-blue-100 text-blue-800',
                  investigating: 'bg-amber-100 text-amber-800',
                  approved: 'bg-green-100 text-green-800',
                  rejected: 'bg-red-100 text-red-800',
                  closed: 'bg-stone-100 text-stone-800'
                }[claim.status] || 'bg-stone-100 text-stone-800';

                return (
                  <div key={claim.id} className="bg-white rounded-2xl p-4 shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setSelectedClaim(claim); setProviderResponse(claim.provider_response || ''); }} data-testid={`warranty-claim-${claim.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColor}`}>
                        {claim.status?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                      </span>
                      <span className="text-xs text-slate-400">#{claim.id?.slice(0, 8)}</span>
                    </div>
                    <p className="font-semibold text-slate-900 text-sm">{claim.reason}</p>
                    <p className="text-sm text-slate-600 mt-1 line-clamp-2">{claim.description}</p>
                    <div className="flex items-center gap-3 text-xs text-slate-400 mt-3">
                      <span>{claim.booking_service || 'Service'}</span>
                      <span>Booking #{claim.booking_id?.slice(0, 8)}</span>
                    </div>
                    {claim.refund_amount > 0 && claim.status === 'approved' && (
                      <div className="mt-2 p-2 bg-green-50 rounded-lg"><p className="text-xs text-green-700 font-medium">Refund: P{claim.refund_amount?.toLocaleString()}</p></div>
                    )}
                    {claim.admin_resolution && (
                      <div className="mt-2 p-2 bg-green-50 rounded-lg"><p className="text-xs text-green-700"><strong>Resolution:</strong> {claim.admin_resolution}</p></div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}
      </main>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Submit Support Ticket</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {/* Category Selection */}
            <div>
              <Label>What do you need help with?</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {Object.entries(categoryConfig).map(([key, config]) => {
                  const Icon = config.icon;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setForm(p => ({ ...p, category: key, reason: '', booking_id: '' }))}
                      className={`flex items-center gap-2 p-3 rounded-xl border-2 transition-all ${
                        form.category === key 
                          ? 'border-amber-500 bg-amber-50' 
                          : 'border-stone-200 hover:border-stone-300'
                      }`}
                      data-testid={`category-${key}`}
                    >
                      <Icon className={`w-5 h-5 ${form.category === key ? 'text-amber-600' : 'text-slate-500'}`} />
                      <span className={`text-sm font-medium ${form.category === key ? 'text-amber-900' : 'text-slate-700'}`}>
                        {config.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
            
            {/* Booking Selection (only for booking category) */}
            {form.category === 'booking' && (
              <div>
                <Label>Select Booking</Label>
                <select
                  value={form.booking_id}
                  onChange={(e) => setForm(p => ({ ...p, booking_id: e.target.value }))}
                  className="w-full h-12 mt-1.5 rounded-lg border border-stone-300 px-3 text-sm"
                  data-testid="dispute-booking-select"
                >
                  <option value="">Choose a booking...</option>
                  {bookings.map(b => (
                    <option key={b.id} value={b.id}>
                      #{b.id.slice(0, 8).toUpperCase()} - {b.status} ({b.scheduled_date})
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            {/* Issue Type */}
            <div>
              <Label>Issue Type</Label>
              <select
                value={form.reason}
                onChange={(e) => setForm(p => ({ ...p, reason: e.target.value }))}
                className="w-full h-12 mt-1.5 rounded-lg border border-stone-300 px-3 text-sm"
                data-testid="dispute-reason-select"
              >
                <option value="">Select issue type...</option>
                {(issueTypesByCategory[form.category] || issueTypesByCategory.other).map(r => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
            
            {/* Description */}
            <div>
              <Label>Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))}
                placeholder="Please describe your issue in detail..."
                className="min-h-[100px] mt-1.5"
                data-testid="dispute-description"
              />
            </div>
            
            <Button
              className="w-full h-12 bg-slate-900 hover:bg-slate-800"
              onClick={handleCreate}
              disabled={submitting}
              data-testid="submit-dispute-btn"
            >
              {submitting ? 'Submitting...' : 'Submit Ticket'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Ticket Detail Dialog */}
      <Dialog open={!!selectedDispute} onOpenChange={() => setSelectedDispute(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Ticket Details</DialogTitle>
          </DialogHeader>
          {selectedDispute && (
            <div className="space-y-4 mt-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${categoryConfig[selectedDispute.category]?.color || categoryConfig.other.color}`}>
                    {categoryConfig[selectedDispute.category]?.label || 'Other'}
                  </span>
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${statusColors[selectedDispute.status]}`}>
                    {selectedDispute.status.charAt(0).toUpperCase() + selectedDispute.status.slice(1)}
                  </span>
                </div>
                <span className="text-sm text-slate-400">#{selectedDispute.id.slice(0, 8)}</span>
              </div>
              
              <div className="bg-stone-50 rounded-xl p-4 space-y-3">
                <div>
                  <p className="text-xs text-slate-400 uppercase font-medium">Issue Type</p>
                  <p className="text-slate-900 font-medium">{selectedDispute.reason}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400 uppercase font-medium">Description</p>
                  <p className="text-slate-700 text-sm">{selectedDispute.description}</p>
                </div>
                {selectedDispute.booking_id && (
                  <div>
                    <p className="text-xs text-slate-400 uppercase font-medium">Booking ID</p>
                    <p className="text-slate-700 text-sm font-mono">{selectedDispute.booking_id}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-slate-400 uppercase font-medium">Submitted</p>
                  <p className="text-slate-700 text-sm">{formatDate(selectedDispute.created_at)}</p>
                </div>
                {selectedDispute.resolution && (
                  <div className="bg-green-50 rounded-lg p-3 mt-2">
                    <p className="text-xs text-green-600 uppercase font-medium">Resolution</p>
                    <p className="text-green-800 text-sm mt-1">{selectedDispute.resolution}</p>
                  </div>
                )}
                {selectedDispute.resolved_at && (
                  <div>
                    <p className="text-xs text-slate-400 uppercase font-medium">Resolved On</p>
                    <p className="text-slate-700 text-sm">{formatDate(selectedDispute.resolved_at)}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Warranty Claim Detail Dialog */}
      <Dialog open={!!selectedClaim} onOpenChange={() => setSelectedClaim(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Warranty Claim Details</DialogTitle>
          </DialogHeader>
          {selectedClaim && (
            <div className="space-y-4 mt-4">
              <div className="flex items-center justify-between">
                <span className={`px-3 py-1.5 rounded-full text-sm font-medium ${
                  { open: 'bg-yellow-100 text-yellow-800', provider_responded: 'bg-blue-100 text-blue-800', investigating: 'bg-amber-100 text-amber-800', approved: 'bg-green-100 text-green-800', rejected: 'bg-red-100 text-red-800', closed: 'bg-stone-100 text-stone-800' }[selectedClaim.status] || 'bg-stone-100 text-stone-800'
                }`}>
                  {selectedClaim.status?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                </span>
                <span className="text-sm text-slate-400">#{selectedClaim.id?.slice(0, 8)}</span>
              </div>
              <div className="bg-stone-50 rounded-xl p-4 space-y-3">
                <div><p className="text-xs text-slate-400 uppercase font-medium">Issue</p><p className="text-slate-900 font-medium">{selectedClaim.reason}</p></div>
                <div><p className="text-xs text-slate-400 uppercase font-medium">Description</p><p className="text-slate-700 text-sm">{selectedClaim.description}</p></div>
                <div><p className="text-xs text-slate-400 uppercase font-medium">Service</p><p className="text-slate-700 text-sm">{selectedClaim.booking_service || 'N/A'}</p></div>
                <div><p className="text-xs text-slate-400 uppercase font-medium">Warranty Until</p><p className="text-slate-700 text-sm">{selectedClaim.warranty_end_date ? new Date(selectedClaim.warranty_end_date).toLocaleDateString() : 'N/A'}</p></div>
              </div>
              {selectedClaim.provider_response && (
                <div className="bg-blue-50 rounded-xl p-4">
                  <p className="text-xs text-blue-600 uppercase font-medium mb-1">Provider Response</p>
                  <p className="text-blue-800 text-sm">{selectedClaim.provider_response}</p>
                </div>
              )}
              {selectedClaim.admin_resolution && (
                <div className="bg-green-50 rounded-xl p-4">
                  <p className="text-xs text-green-600 uppercase font-medium mb-1">Admin Resolution</p>
                  <p className="text-green-800 text-sm">{selectedClaim.admin_resolution}</p>
                </div>
              )}
              {selectedClaim.refund_amount > 0 && (
                <div className="bg-emerald-50 rounded-xl p-4">
                  <p className="text-xs text-emerald-600 uppercase font-medium mb-1">Refund Amount</p>
                  <p className="text-emerald-800 font-bold">P{selectedClaim.refund_amount?.toLocaleString()}</p>
                </div>
              )}
              {isProvider && selectedClaim.status === 'open' && !selectedClaim.provider_response && (
                <div className="border-t pt-4 space-y-3">
                  <Label>Your Response</Label>
                  <Textarea value={providerResponse} onChange={(e) => setProviderResponse(e.target.value)} placeholder="Explain or respond to this warranty claim..." className="min-h-[80px]" data-testid="provider-warranty-response" />
                  <Button className="w-full bg-slate-900 hover:bg-slate-800" onClick={() => handleRespondToClaim(selectedClaim.id)} disabled={isRespondingToClaim || !providerResponse.trim()} data-testid="submit-warranty-response-btn">
                    {isRespondingToClaim ? 'Submitting...' : 'Submit Response'}
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bottom Nav */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-stone-200 px-2 py-2 z-50">
        <div className="flex justify-around max-w-lg mx-auto">
          {navItems.map(item => (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`flex flex-col items-center gap-1 px-3 py-1 ${item.active ? 'text-amber-600' : 'text-slate-400'}`}
            >
              <item.icon className="w-5 h-5" />
              <span className="text-xs">{item.label}</span>
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
}
